import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShoppingCart, Search, Package, Truck } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    client_id: "",
    client_name: "",
    pickup_address: "",
    delivery_address: "",
    material_type: "",
    quantity: "",
    unit: "tons",
    expected_delivery_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    priority: "normal",
    special_instructions: "",
    estimated_cost: "",
    freight_amount: "",
    advance_amount: "",
    estimated_transit_hours: "",
    status: "pending"
  });
  const [assignData, setAssignData] = useState({
    assigned_vendor_id: "",
    assigned_vendor_name: "",
    assigned_driver_id: "",
    assigned_driver_name: "",
    driver_type: "",
    vehicle_number: "",
    freight_amount: "",
    advance_amount: "",
    vendor_rate: "",
    vendor_advance: "",
    status: "assigned"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, clientsRes, vendorsRes, employeesRes] = await Promise.all([
        axios.get(`${API_URL}/orders`),
        axios.get(`${API_URL}/clients`),
        axios.get(`${API_URL}/vendors`),
        axios.get(`${API_URL}/employees`)
      ]);
      setOrders(ordersRes.data);
      setClients(clientsRes.data);
      setVendors(vendorsRes.data.filter(v => v.status === "active"));
      setEmployees(employeesRes.data.filter(e => e.designation === "Driver" && e.status === "active"));
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        quantity: parseFloat(formData.quantity),
        estimated_cost: parseFloat(formData.estimated_cost || 0),
        freight_amount: parseFloat(formData.freight_amount || formData.estimated_cost || 0),
        estimated_transit_hours: formData.estimated_transit_hours ? parseFloat(formData.estimated_transit_hours) : null
      };

      if (selectedOrder) {
        await axios.put(`${API_URL}/orders/${selectedOrder.id}`, submitData);
        toast.success("Order updated successfully");
      } else {
        await axios.post(`${API_URL}/orders`, submitData);
        toast.success("Order created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      // Client side amounts
      const freightAmt = parseFloat(assignData.freight_amount || selectedOrder?.freight_amount || selectedOrder?.estimated_cost || 0);
      const advanceAmt = parseFloat(assignData.advance_amount || 0);
      const balanceAmt = freightAmt - advanceAmt;
      
      // Vendor side amounts
      const vendorRate = parseFloat(assignData.vendor_rate || 0);
      const vendorAdvance = parseFloat(assignData.vendor_advance || 0);
      const vendorBalance = vendorRate - vendorAdvance;
      
      const submitData = {
        ...assignData,
        freight_amount: freightAmt,
        advance_amount: advanceAmt,
        balance_amount: balanceAmt,
        vendor_rate: vendorRate,
        vendor_advance: vendorAdvance,
        vendor_balance: vendorBalance
      };
      const response = await axios.put(`${API_URL}/orders/${selectedOrder.id}`, submitData);
      
      // Check if load slip was auto-created
      if (response.data.load_slip_id) {
        const clientMsg = `Client: Freight ₹${freightAmt.toLocaleString()}` + (advanceAmt > 0 ? `, Advance ₹${advanceAmt.toLocaleString()}` : '');
        const vendorMsg = vendorRate > 0 ? ` | Vendor: Rate ₹${vendorRate.toLocaleString()}` + (vendorAdvance > 0 ? `, Advance ₹${vendorAdvance.toLocaleString()}` : '') : '';
        toast.success(`Trip created! Load Slip generated. ${clientMsg}${vendorMsg}`);
      } else {
        toast.success("Order assigned successfully");
      }
      setAssignDialogOpen(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to assign order");
    }
  };

  const handleEdit = (order) => {
    setSelectedOrder(order);
    setFormData({
      date: order.date,
      client_id: order.client_id,
      client_name: order.client_name,
      pickup_address: order.pickup_address,
      delivery_address: order.delivery_address,
      material_type: order.material_type,
      quantity: order.quantity.toString(),
      unit: order.unit,
      expected_delivery_date: order.expected_delivery_date,
      priority: order.priority,
      special_instructions: order.special_instructions || "",
      estimated_cost: order.estimated_cost?.toString() || "",
      freight_amount: order.freight_amount?.toString() || order.estimated_cost?.toString() || "",
      estimated_transit_hours: order.estimated_transit_hours?.toString() || "",
      status: order.status
    });
    setDialogOpen(true);
  };

  const handleAssignOpen = (order) => {
    setSelectedOrder(order);
    setAssignData({
      assigned_vendor_id: order.assigned_vendor_id || "",
      assigned_vendor_name: order.assigned_vendor_name || "",
      assigned_driver_id: order.assigned_driver_id || "",
      assigned_driver_name: order.assigned_driver_name || "",
      driver_type: "",
      vehicle_number: order.vehicle_number || "",
      freight_amount: order.freight_amount?.toString() || order.estimated_cost?.toString() || "",
      advance_amount: order.advance_amount?.toString() || "",
      vendor_rate: order.vendor_rate?.toString() || "",
      vendor_advance: order.vendor_advance?.toString() || "",
      status: "assigned"
    });
    setAssignDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/orders/${selectedOrder.id}`);
      toast.success("Order deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete order");
    }
  };

  const resetForm = () => {
    setSelectedOrder(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      client_id: "",
      client_name: "",
      pickup_address: "",
      delivery_address: "",
      material_type: "",
      quantity: "",
      unit: "tons",
      expected_delivery_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      priority: "normal",
      special_instructions: "",
      estimated_cost: "",
      freight_amount: "",
      estimated_transit_hours: "",
      status: "pending"
    });
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setFormData({
      ...formData,
      client_id: clientId,
      client_name: client?.company_name || "",
      pickup_address: client?.address || ""
    });
  };

  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    setAssignData({
      ...assignData,
      assigned_vendor_id: vendorId,
      assigned_vendor_name: vendor?.name || "",
      assigned_driver_id: "",
      assigned_driver_name: "",
      driver_type: ""
    });
  };

  const handleDriverChange = (driverValue) => {
    // driverValue format: "vendor:index:name" or "company:id:name"
    const parts = driverValue.split(':');
    const driverType = parts[0];
    const driverName = parts.slice(2).join(':'); // Handle names with colons
    
    setAssignData({
      ...assignData,
      assigned_driver_id: driverValue,
      assigned_driver_name: driverName,
      driver_type: driverType
    });
  };

  // Get selected vendor's drivers
  const getSelectedVendorDrivers = () => {
    if (!assignData.assigned_vendor_id) return [];
    const vendor = vendors.find(v => v.id === assignData.assigned_vendor_id);
    return vendor?.drivers || [];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "border-yellow-500/50 text-yellow-400 bg-yellow-500/10",
      confirmed: "border-blue-500/50 text-blue-400 bg-blue-500/10",
      assigned: "border-purple-500/50 text-purple-400 bg-purple-500/10",
      in_progress: "border-cyan-500/50 text-cyan-400 bg-cyan-500/10",
      completed: "border-green-500/50 text-green-400 bg-green-500/10",
      cancelled: "border-red-500/50 text-red-400 bg-red-500/10"
    };
    return styles[status] || styles.pending;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: "border-gray-500/50 text-gray-400 bg-gray-500/10",
      normal: "border-blue-500/50 text-blue-400 bg-blue-500/10",
      high: "border-orange-500/50 text-orange-400 bg-orange-500/10",
      urgent: "border-red-500/50 text-red-400 bg-red-500/10"
    };
    return styles[priority] || styles.normal;
  };

  const filteredOrders = orders.filter(order =>
    order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.material_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse font-heading text-xl">Loading orders...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <ShoppingCart className="w-8 h-8 text-accent" />
            Order Management
          </h1>
          <p className="text-muted-foreground mt-1">Create and manage transport orders</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="glow-primary bg-primary hover:bg-primary/90"
          data-testid="create-order-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Order
        </Button>
      </div>

      {/* Search */}
      <Card className="border-border bg-card">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by order number, client, or material..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-secondary/50 border-border"
              data-testid="order-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">All Orders ({filteredOrders.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Order No.</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Client</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Material</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Route</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Delivery Date</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Priority</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Status</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No orders found. Create your first order to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrders.map((order) => (
                    <TableRow key={order.id} className="border-border table-row-hover" data-testid={`order-row-${order.id}`}>
                      <TableCell className="font-mono text-primary">{order.order_number}</TableCell>
                      <TableCell className="font-medium text-foreground">{order.client_name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {order.material_type} ({order.quantity} {order.unit})
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        <div className="flex flex-col">
                          <span className="truncate max-w-[150px]">{order.pickup_address}</span>
                          <span className="text-xs text-primary">→ {order.delivery_address}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{order.expected_delivery_date}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getPriorityBadge(order.priority)}>
                          {order.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getStatusBadge(order.status)}>
                          {order.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {order.status === "pending" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-green-400 hover:text-green-300"
                              onClick={() => handleAssignOpen(order)}
                              data-testid={`assign-order-${order.id}`}
                              title="Assign Vendor/Driver"
                            >
                              <Truck className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(order)}
                            data-testid={`edit-order-${order.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => { setSelectedOrder(order); setDeleteDialogOpen(true); }}
                            data-testid={`delete-order-${order.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedOrder ? "Edit Order" : "Create New Order"}
            </DialogTitle>
            <DialogDescription>
              {selectedOrder ? "Update order details" : "Enter the details for the new transport order"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Order Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-secondary/50 border-border"
                  required
                  data-testid="order-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client">Client</Label>
                <Select value={formData.client_id} onValueChange={handleClientChange}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="order-client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pickup_address">Pickup Address</Label>
              <Input
                id="pickup_address"
                value={formData.pickup_address}
                onChange={(e) => setFormData({ ...formData, pickup_address: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="Warehouse A, Mumbai"
                required
                data-testid="order-pickup-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="delivery_address">Delivery Address</Label>
              <Input
                id="delivery_address"
                value={formData.delivery_address}
                onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="Factory B, Delhi"
                required
                data-testid="order-delivery-input"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="material_type">Material Type</Label>
                <Input
                  id="material_type"
                  value={formData.material_type}
                  onChange={(e) => setFormData({ ...formData, material_type: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="Steel Coils"
                  required
                  data-testid="order-material-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  step="0.01"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="10"
                  required
                  data-testid="order-quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit">Unit</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="order-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tons">Tons</SelectItem>
                    <SelectItem value="kg">Kg</SelectItem>
                    <SelectItem value="pieces">Pieces</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expected_delivery_date">Expected Delivery Date</Label>
                <Input
                  id="expected_delivery_date"
                  type="date"
                  value={formData.expected_delivery_date}
                  onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })}
                  className="bg-secondary/50 border-border"
                  required
                  data-testid="order-expected-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="order-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estimated_cost">Estimated Cost (₹)</Label>
                <Input
                  id="estimated_cost"
                  type="number"
                  value={formData.estimated_cost}
                  onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="50000"
                  data-testid="order-cost-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="freight_amount">Freight Amount (₹)</Label>
                <Input
                  id="freight_amount"
                  type="number"
                  value={formData.freight_amount}
                  onChange={(e) => setFormData({ ...formData, freight_amount: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="45000"
                  data-testid="order-freight-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimated_transit_hours">Estimated Transit Time (Hours)</Label>
              <Input
                id="estimated_transit_hours"
                type="number"
                step="0.5"
                value={formData.estimated_transit_hours}
                onChange={(e) => setFormData({ ...formData, estimated_transit_hours: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="24"
                data-testid="order-transit-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="special_instructions">Special Instructions (Optional)</Label>
              <Textarea
                id="special_instructions"
                value={formData.special_instructions}
                onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                className="bg-secondary/50 border-border min-h-[80px]"
                placeholder="Any special handling or delivery instructions..."
                data-testid="order-instructions-input"
              />
            </div>

            {selectedOrder && (
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="order-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="assigned">Assigned</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="glow-primary bg-primary" data-testid="order-submit-btn">
                {selectedOrder ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Assign Order & Create Trip</DialogTitle>
            <DialogDescription>
              Assign vendor, driver and vehicle for order {selectedOrder?.order_number}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssign} className="space-y-4">
            {/* Auto-creation notice */}
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
              <p className="text-blue-400 font-medium">⚡ Auto-Creation Enabled</p>
              <p className="text-muted-foreground text-xs mt-1">
                When assigned, a Load Slip and Client Ledger entry will be created automatically.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="vendor">Vendor</Label>
              <Select value={assignData.assigned_vendor_id} onValueChange={handleVendorChange}>
                <SelectTrigger className="bg-secondary/50 border-border" data-testid="assign-vendor-select">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="driver">Driver</Label>
              <Select value={assignData.assigned_driver_id} onValueChange={handleDriverChange}>
                <SelectTrigger className="bg-secondary/50 border-border" data-testid="assign-driver-select">
                  <SelectValue placeholder="Select driver" />
                </SelectTrigger>
                <SelectContent>
                  {/* Vendor Drivers */}
                  {getSelectedVendorDrivers().length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-orange-400 bg-orange-500/10">
                        Vendor Drivers
                      </div>
                      {getSelectedVendorDrivers().map((driver, idx) => (
                        <SelectItem 
                          key={`vendor-${idx}`} 
                          value={`vendor:${idx}:${driver.name}`}
                        >
                          {driver.name} {driver.mobile && `(${driver.mobile})`}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  
                  {/* Company Drivers */}
                  {employees.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-blue-400 bg-blue-500/10 mt-1">
                        Company Drivers
                      </div>
                      {employees.map((driver) => (
                        <SelectItem 
                          key={`company-${driver.id}`} 
                          value={`company:${driver.id}:${driver.name}`}
                        >
                          {driver.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  
                  {/* No drivers message */}
                  {getSelectedVendorDrivers().length === 0 && employees.length === 0 && (
                    <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                      No drivers available. Add drivers in Vendors or Employees page.
                    </div>
                  )}
                </SelectContent>
              </Select>
              {assignData.driver_type && (
                <p className="text-xs text-muted-foreground">
                  {assignData.driver_type === 'vendor' ? '🚛 Vendor Driver' : '🏢 Company Driver'}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="vehicle_number">Vehicle Number</Label>
              <Input
                id="vehicle_number"
                value={assignData.vehicle_number}
                onChange={(e) => setAssignData({ ...assignData, vehicle_number: e.target.value.toUpperCase() })}
                className="bg-secondary/50 border-border"
                placeholder="MH-12-AB-1234"
                required
                data-testid="assign-vehicle-input"
              />
            </div>

            {/* Payment Details Section */}
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg space-y-3">
              <h4 className="font-medium text-sm text-green-400">Client Payment (Money IN)</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="freight_amount" className="text-xs">Total Freight (₹)</Label>
                  <Input
                    id="freight_amount"
                    type="number"
                    value={assignData.freight_amount}
                    onChange={(e) => setAssignData({ ...assignData, freight_amount: e.target.value })}
                    className="bg-secondary/50 border-border"
                    placeholder="45000"
                    required
                    data-testid="assign-freight-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="advance_amount" className="text-xs">Advance Received (₹)</Label>
                  <Input
                    id="advance_amount"
                    type="number"
                    value={assignData.advance_amount}
                    onChange={(e) => setAssignData({ ...assignData, advance_amount: e.target.value })}
                    className="bg-secondary/50 border-border"
                    placeholder="10000"
                    data-testid="assign-advance-input"
                  />
                </div>
              </div>
              
              {/* Auto-calculated balance display */}
              <div className="flex justify-between items-center pt-2 border-t border-green-500/30">
                <span className="text-sm text-muted-foreground">Client Balance Due:</span>
                <span className="font-bold text-green-400">
                  ₹{((parseFloat(assignData.freight_amount) || 0) - (parseFloat(assignData.advance_amount) || 0)).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Vendor Payment Section */}
            <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg space-y-3">
              <h4 className="font-medium text-sm text-orange-400">Vendor Payment (Money OUT)</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="vendor_rate" className="text-xs">Vendor Rate (₹)</Label>
                  <Input
                    id="vendor_rate"
                    type="number"
                    value={assignData.vendor_rate}
                    onChange={(e) => setAssignData({ ...assignData, vendor_rate: e.target.value })}
                    className="bg-secondary/50 border-border"
                    placeholder="35000"
                    data-testid="assign-vendor-rate-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="vendor_advance" className="text-xs">Vendor Advance (₹)</Label>
                  <Input
                    id="vendor_advance"
                    type="number"
                    value={assignData.vendor_advance}
                    onChange={(e) => setAssignData({ ...assignData, vendor_advance: e.target.value })}
                    className="bg-secondary/50 border-border"
                    placeholder="5000"
                    data-testid="assign-vendor-advance-input"
                  />
                </div>
              </div>
              
              {/* Auto-calculated vendor balance display */}
              <div className="flex justify-between items-center pt-2 border-t border-orange-500/30">
                <span className="text-sm text-muted-foreground">Vendor Balance Due:</span>
                <span className="font-bold text-orange-400">
                  ₹{((parseFloat(assignData.vendor_rate) || 0) - (parseFloat(assignData.vendor_advance) || 0)).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Profit Summary */}
            {(parseFloat(assignData.freight_amount) || 0) > 0 && (parseFloat(assignData.vendor_rate) || 0) > 0 && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-blue-400">Expected Profit:</span>
                  <span className={`font-bold text-lg ${(parseFloat(assignData.freight_amount) || 0) - (parseFloat(assignData.vendor_rate) || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ₹{((parseFloat(assignData.freight_amount) || 0) - (parseFloat(assignData.vendor_rate) || 0)).toLocaleString()}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Profit = Client Freight - Vendor Rate
                </p>
              </div>
            )}
              
            <p className="text-xs text-muted-foreground">
              Ledger entries will be created automatically. UTR confirmation required for payments.
            </p>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="glow-primary bg-primary" data-testid="assign-submit-btn">
                Assign & Create Trip
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Delete Order</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete order "{selectedOrder?.order_number}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-order">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
